import { useEffect, useState } from 'react';
import {
  HoaDonMuaDichVu,
  HoaDonMuaDichVuInput,
  HoaDonMuaDichVuResponse
} from '@/types/schemas/hoa-don-mua-dich-vu.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseHoaDonMuaDichVuReturn {
  hoaDonMuaDichVus: HoaDonMuaDichVu[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addHoaDonMuaDichVu: (newHoaDonMuaDichVu: HoaDonMuaDichVuInput) => Promise<HoaDonMuaDichVu>;
  updateHoaDonMuaDichVu: (uuid: string, updatedHoaDonMuaDichVu: HoaDonMuaDichVuInput) => Promise<HoaDonMuaDichVu>;
  deleteHoaDonMuaDichVu: (uuid: string) => Promise<void>;
  refreshHoaDonMuaDichVus: () => Promise<void>;
  getHoaDonMuaDichVuByCustomer: (customerId: string) => Promise<HoaDonMuaDichVu[]>;
  getHoaDonMuaDichVuDetail: (uuid: string) => Promise<[]>;
}

/**
 * Hook for managing HoaDonMuaDichVu (Purchase Invoice) data
 *
 * This hook provides functions to fetch, create, update, and delete purchase invoices and their details.
 */
export const useHoaDonMuaDichVu = (initialHoaDonMuaDichVus: HoaDonMuaDichVu[] = []): UseHoaDonMuaDichVuReturn => {
  const [hoaDonMuaDichVus, setHoaDonMuaDichVus] = useState<HoaDonMuaDichVu[]>(initialHoaDonMuaDichVus);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchHoaDonMucDichVus = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonMuaDichVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/`
      );
      setHoaDonMuaDichVus(response.data.results);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonMuaDichVuByCustomer = async (customerId: string): Promise<HoaDonMuaDichVu[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonMuaDichVuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales invoices by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addHoaDonMuaDichVu = async (newHoaDonMuaDichVu: HoaDonMuaDichVuInput): Promise<HoaDonMuaDichVu> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<HoaDonMuaDichVu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/`,
        newHoaDonMuaDichVu
      );

      const addedHoaDonMuaDichVu = response.data;
      setHoaDonMuaDichVus(prev => [...prev, addedHoaDonMuaDichVu]);
      return addedHoaDonMuaDichVu;
    } catch (error) {
      console.error('Error adding sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateHoaDonMuaDichVu = async (
    uuid: string,
    updatedHoaDonMuaDichVu: HoaDonMuaDichVuInput
  ): Promise<HoaDonMuaDichVu> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<HoaDonMuaDichVu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/${uuid}/`,
        updatedHoaDonMuaDichVu
      );

      const updatedData = response.data;
      setHoaDonMuaDichVus(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteHoaDonMuaDichVu = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/${uuid}/`);
      setHoaDonMuaDichVus(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonMuaDichVuDetail = async (uuid: string): Promise<any> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<any>(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales invoice detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return {
    hoaDonMuaDichVus,
    isLoading,
    isLoadingDetail,
    addHoaDonMuaDichVu,
    updateHoaDonMuaDichVu,
    deleteHoaDonMuaDichVu,
    refreshHoaDonMuaDichVus: fetchHoaDonMucDichVus,
    getHoaDonMuaDichVuByCustomer,
    getHoaDonMuaDichVuDetail
  };
};

export const fetchAllHoaDonMuaDichVu = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<HoaDonMuaDichVu[]> => {
  let page = 1;
  let all: HoaDonMuaDichVu[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<HoaDonMuaDichVuResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.HOA_DON_MUA_DICH_VU}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
